import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { RichTextEditorComponent } from 'src/app/components/shared/rich-text-editor/rich-text-editor.component';
import { LoggerService } from '../../../services/logger.service';

type Channel = 'email' | 'sms';

interface TemplateDef {
  key: string;
  label: string;
  description: string;
  // Optional. When omitted, the template has no enable/disable toggle and
  // is treated as always-on: the underlying send is invoked directly from
  // user actions (e.g. New Enrollment fires when staff create the enrollment).
  enableField?: string;
  // Optional. Template-key to mirror the on/off state from, for body variants
  // that share their parent's enable flag (e.g. Multiple Lesson Reminder
  // inherits from Lesson Reminder).
  inheritsEnableFrom?: string;
  subjectField?: string;
  bodyField: string;
  placeholders: string[];
  defaultSubject?: string;
  defaultBody: string;
  hasIntervals?: boolean;
}

interface ChannelConfig {
  configFieldName: string;
  templates: TemplateDef[];
}

const EMAIL_STUDENT_PLACEHOLDERS = [
  '__studentFirstName__',
  '__studentLastName__',
  '__studentName__',
];

const EMAIL_STUDIO_PLACEHOLDERS = [
  '__studioName__',
  '__studioAddress__',
  '__studioEmail__',
  '__studioNumber__',
];

const EMAIL_GIFT_CERTIFICATE_PLACEHOLDERS = [
  '__recipientName__',
  '__giftCertificateCode__',
  '__giftCertificateAmount__',
  '__giftCertificateExpirationDate__',
  // The purchaser's message block. Left out of the template, it is added after the body.
  '__giftMessage__',
  ...EMAIL_STUDIO_PLACEHOLDERS,
];

const EMAIL_SINGLE_LESSON_PLACEHOLDERS = [
  ...EMAIL_STUDENT_PLACEHOLDERS,
  '__dateTime__',
  '__instructorName__',
  ...EMAIL_STUDIO_PLACEHOLDERS,
  '__AddToCalendar__',
];

const EMAIL_GROUPED_LESSON_PLACEHOLDERS = [
  ...EMAIL_STUDENT_PLACEHOLDERS,
  '__lessonList__',
  '__lessonListWithInstructors__',
  '__lessonCount__',
  '__lessonDate__',
  ...EMAIL_STUDIO_PLACEHOLDERS,
];

const EMAIL_ONLINE_BOOKING_PLACEHOLDERS = [
  ...EMAIL_STUDENT_PLACEHOLDERS,
  '__dateTime__',
  '__lessonName__',
  '__lessonDuration__',
  ...EMAIL_STUDIO_PLACEHOLDERS,
  '__AddToCalendar__',
];

const SMS_ONLINE_BOOKING_PLACEHOLDERS = [
  '{$studentFirstName}',
  '{$studentName}',
  '{$dateTime}',
  '{$lessonName}',
  '{$lessonDuration}',
  '{$studioName}',
  '{$studioPhoneNumber}',
];

const SMS_SINGLE_LESSON_PLACEHOLDERS = [
  '{$studentName}',
  '{$dateTime}',
  '{$instructorName}',
  '{$studioPhoneNumber}',
];

const SMS_GROUPED_LESSON_PLACEHOLDERS = [
  '{$studentFirstName}',
  '{$lessonList}',
  '{$lessonListWithInstructors}',
  '{$lessonCount}',
  '{$lessonDate}',
  '{$studioPhoneNumber}',
];

const SMS_GIFT_CERTIFICATE_PLACEHOLDERS = [
  '{$recipientName}',
  '{$recipientFirstName}',
  '{$purchaserName}',
  '{$giftCertificateAmount}',
  '{$giftCertificateCode}',
  '{$giftCertificateExpirationDate}',
  '{$giftMessage}',
  '{$giftUrl}',
  '{$balanceUrl}',
  '{$studioName}',
  '{$studioPhoneNumber}',
];

const EMAIL_CONFIG: ChannelConfig = {
  configFieldName: 'field_email_template_reference',
  templates: [
    {
      key: 'lessonReminder',
      label: 'Lesson Reminder',
      description: 'Sent before a single upcoming lesson.',
      enableField: 'field_enable_lesson_reminder',
      subjectField: 'field_lesson_reminder_subject',
      bodyField: 'field_lesson_reminder_template',
      placeholders: EMAIL_SINGLE_LESSON_PLACEHOLDERS,
      defaultSubject: 'Lesson Reminder: __dateTime__',
      defaultBody:
        `Hello __studentFirstName__,\n\n` +
        `Just a friendly reminder that you have a lesson scheduled for __dateTime__ with __instructorName__.\n\n` +
        `Make sure to arrive on time and be prepared to have fun. Please do not respond to this message directly. ` +
        `If you have any questions or need to reschedule, don't hesitate to get in touch with us using the studio email __studioEmail__ or call/text the studio at __studioNumber__.\n\n` +
        `__AddToCalendar__\n\n` +
        `See you soon!`,
      hasIntervals: true,
    },
    {
      key: 'groupedLessonReminder',
      label: 'Multiple Lesson Reminder',
      description: 'Sent when a student has 2+ lessons on the same day, in place of the per-lesson reminder. Controlled by the Lesson Reminder on/off switch.',
      inheritsEnableFrom: 'lessonReminder',
      subjectField: 'field_grouped_reminder_subject',
      bodyField: 'field_grouped_reminder_template',
      placeholders: EMAIL_GROUPED_LESSON_PLACEHOLDERS,
      defaultSubject: 'Your __lessonCount__ Upcoming Lessons for __lessonDate__',
      defaultBody:
        `<p>Hello __studentFirstName__,</p>\n` +
        `<p>Just a friendly reminder that you have <strong>__lessonCount__ lessons</strong> scheduled for <strong>__lessonDate__</strong>:</p>\n` +
        `__lessonList__\n` +
        `<p>Please do not respond to this message directly. If you have any questions or need to reschedule, don't hesitate to get in touch with us using the studio email __studioEmail__ or call/text the studio at __studioNumber__.</p>\n` +
        `<p>See you soon!</p>`,
    },
    {
      key: 'newEnrollment',
      label: 'New Enrollment',
      description: 'Sent when a student is enrolled in a new package.',
      subjectField: 'field_new_enrollment_subject',
      bodyField: 'field_new_enrollment_template',
      placeholders: [...EMAIL_STUDENT_PLACEHOLDERS, ...EMAIL_STUDIO_PLACEHOLDERS],
      defaultSubject: 'Exciting News! Your Enrollment at __studioName__ is Confirmed',
      defaultBody:
        `<p>Hello __studentName__,</p>\n` +
        `<p>Congratulations on continuing on with your dancing at __studioName__!</p>\n` +
        `<p>Location: __studioAddress__</p>\n` +
        `<h3>We're Here for You</h3>\n` +
        `<p>Should you have any questions or need assistance as you prepare for your next lesson, please don't hesitate to reach out to us at __studioEmail__ or __studioNumber__.</p>\n` +
        `<p>We're so excited to have you with the Arthur Murray family.</p>\n` +
        `<p>See you soon!</p>`,
    },
    {
      key: 'giftCertificate',
      label: 'Gift Certificate',
      description: "Sent to the recipient when a gift certificate is issued or resent. Includes the certificate code and, when one was entered, the purchaser's gift message.",
      subjectField: 'field_gift_certificate_subject',
      bodyField: 'field_gift_certificate_template',
      placeholders: EMAIL_GIFT_CERTIFICATE_PLACEHOLDERS,
      defaultSubject: 'A Gift Certificate from __studioName__',
      defaultBody:
        `<p>Dear __recipientName__,</p>\n` +
        `<p>We're excited to let you know that you've received a gift certificate for __studioName__!</p>\n` +
        `<h3>Gift Certificate Details</h3>\n` +
        `<p>Amount: __giftCertificateAmount__<br>Code: __giftCertificateCode__<br>Valid until: __giftCertificateExpirationDate__</p>\n` +
        `<p>You can redeem this gift certificate at:<br>__studioName__<br>__studioAddress__<br>Phone: __studioNumber__<br>Email: __studioEmail__</p>\n` +
        `<p>We look forward to seeing you on the dance floor!</p>`,
    },
    {
      key: 'onlineBooking',
      label: 'Online Booking Confirmation',
      description: 'Sent to a visitor the moment they book an intro lesson on the studio website. Always on while online booking is enabled.',
      subjectField: 'field_online_booking_subject',
      bodyField: 'field_online_booking_template',
      placeholders: EMAIL_ONLINE_BOOKING_PLACEHOLDERS,
      defaultSubject: 'Your intro lesson is booked: __dateTime__',
      defaultBody:
        `<p>Hi __studentFirstName__,</p>\n` +
        `<p>Your intro lesson at <strong>__studioName__</strong> is booked for <strong>__dateTime__</strong>.</p>\n` +
        `<p>__studioAddress__</p>\n` +
        `<p>Need to change it? Call us at __studioNumber__.</p>\n` +
        `<p>No dance experience needed. We look forward to meeting you!</p>\n` +
        `__AddToCalendar__`,
    },
  ],
};

const SMS_CONFIG: ChannelConfig = {
  configFieldName: 'field_sms_template_settings_ref',
  templates: [
    {
      key: 'lessonReminder',
      label: 'Lesson Reminder',
      description: 'Sent before a single upcoming lesson. "Reply STOP to opt out" is added automatically about once a month per student, so there is no need to include it in the template.',
      enableField: 'field_enable_lesson_reminder',
      bodyField: 'field_lesson_reminder_template',
      placeholders: SMS_SINGLE_LESSON_PLACEHOLDERS,
      defaultBody:
        `Hello {$studentName}, just a reminder of your upcoming lesson with {$instructorName} on {$dateTime}. ` +
        `Feel free to call us at {$studioPhoneNumber}. Please reply "CONFIRM" or "C" to confirm your attendance.`,
      hasIntervals: true,
    },
    {
      key: 'groupedLessonReminder',
      label: 'Multiple Lesson Reminder',
      description: 'Sent when a student has 2+ lessons on the same day, in place of the per-lesson reminder. Controlled by the Lesson Reminder on/off switch. "Reply STOP to opt out" is added automatically about once a month per student.',
      inheritsEnableFrom: 'lessonReminder',
      bodyField: 'field_grouped_reminder_template',
      placeholders: SMS_GROUPED_LESSON_PLACEHOLDERS,
      defaultBody:
        `Hi {$studentFirstName}! You have {$lessonCount} lessons scheduled for {$lessonDate}:\n` +
        `{$lessonList}\n` +
        `Please call {$studioPhoneNumber} to Confirm. See you soon!`,
    },
    {
      key: 'onlineBooking',
      label: 'Online Booking Confirmation',
      description: 'Sent to a visitor the moment they book an intro lesson on the studio website, only when they ticked the text-message consent box. "Reply STOP to opt out" is always added, as this is the first text they receive.',
      bodyField: 'field_online_booking_template',
      placeholders: SMS_ONLINE_BOOKING_PLACEHOLDERS,
      defaultBody: `{$studioName}: your intro lesson is booked for {$dateTime}. Questions? Call {$studioPhoneNumber}.`,
    },
    {
      key: 'giftCertificate',
      label: 'Gift Certificate',
      description: 'Sent to the recipient when staff choose Send Text Message on a gift certificate. Reads like a gift, not a receipt: the sender and amount first, the purchaser\'s note in quotes ({$giftMessage}, or nothing when none was entered), then the link to view the certificate ({$giftUrl} shows a preview card in the recipient\'s messaging app; {$balanceUrl} is the plain balance page). US and Canadian numbers also get the gift card image attached as an MMS. "Reply STOP to opt out" is always added, as this is usually the first text the recipient gets from the studio.',
      bodyField: 'field_gift_certificate_template',
      placeholders: SMS_GIFT_CERTIFICATE_PLACEHOLDERS,
      defaultBody:
        `{$recipientFirstName}, you've got a gift! {$purchaserName} sent you a {$giftCertificateAmount} gift certificate to {$studioName}.\n` +
        `{$giftMessage}\n` +
        `View your gift: {$giftUrl}`,
    },
  ],
};

@Component({
  selector: 'app-communication-templates',
  templateUrl: './communication-templates.component.html',
  styleUrls: ['./communication-templates.component.css'],
})
export class CommunicationTemplatesComponent implements OnInit {
  channel: Channel = 'email';
  selectedKey: Record<Channel, string> = {
    email: EMAIL_CONFIG.templates[0].key,
    sms: SMS_CONFIG.templates[0].key,
  };
  forms: Record<Channel, FormGroup> = { email: null!, sms: null! };
  /** Starts true: ngOnInit fires the email and sms loads together, and the
   *  template must not render "Disabled" before either resolves. */
  showProgressSpinner = true;
  successMessage = '';
  errorMessage = '';

  @ViewChild('rte') rte?: RichTextEditorComponent;

  channelConfigs: Record<Channel, ChannelConfig> = {
    email: EMAIL_CONFIG,
    sms: SMS_CONFIG,
  };

  constructor(
    private fb: FormBuilder,
    private rest: DrupalRESTService,
  ) {}

  ngOnInit(): void {
    this.forms.email = this.buildForm(EMAIL_CONFIG);
    this.forms.sms = this.buildForm(SMS_CONFIG);
    this.load('email');
    this.load('sms');
  }

  private buildForm(cfg: ChannelConfig): FormGroup {
    const group: Record<string, any> = {
      enable24HourReminder: [false],
      enable48HourReminder: [false],
      enable72HourReminder: [false],
    };
    for (const t of cfg.templates) {
      group[`${t.key}_enabled`] = [false];
      group[`${t.key}_subject`] = [''];
      group[`${t.key}_body`] = [''];
    }
    return this.fb.group(group);
  }

  get config(): ChannelConfig {
    return this.channelConfigs[this.channel];
  }

  get form(): FormGroup {
    return this.forms[this.channel];
  }

  get templates(): TemplateDef[] {
    return this.config.templates;
  }

  get selected(): TemplateDef {
    return this.templates.find(t => t.key === this.selectedKey[this.channel])!;
  }

  selectTemplate(key: string): void {
    this.selectedKey[this.channel] = key;
    this.successMessage = '';
    this.errorMessage = '';
  }

  changeChannel(channel: Channel): void {
    this.channel = channel;
    this.successMessage = '';
    this.errorMessage = '';
  }

  isEnabled(t: TemplateDef): boolean {
    const sourceKey = t.inheritsEnableFrom ?? t.key;
    return !!this.form?.get(`${sourceKey}_enabled`)?.value;
  }

  load(channel: Channel): void {
    const cfg = this.channelConfigs[channel];
    const form = this.forms[channel];
    this.showProgressSpinner = true;
    this.rest
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${cfg.configFieldName}`)
      .subscribe(
        response => {
          const intervals = response?.['field_lesson_reminder_intervals'] || [];
          const patch: Record<string, any> = {
            enable24HourReminder: intervals.some((i: any) => i.value === '24_hour'),
            enable48HourReminder: intervals.some((i: any) => i.value === '48_hour'),
            enable72HourReminder: intervals.some((i: any) => i.value === '72_hour'),
          };
          for (const t of cfg.templates) {
            if (t.enableField) {
              const enabledArr = response?.[t.enableField];
              patch[`${t.key}_enabled`] = enabledArr?.length > 0 ? !!enabledArr[0].value : false;
            } else {
              // Always-on template: the toggle isn't shown, but the form key
              // still exists so other code that reads it stays happy.
              patch[`${t.key}_enabled`] = true;
            }
            if (t.subjectField) {
              const subjectArr = response?.[t.subjectField];
              patch[`${t.key}_subject`] = subjectArr?.length > 0 ? subjectArr[0].value : (t.defaultSubject ?? '');
            }
            const bodyArr = response?.[t.bodyField];
            patch[`${t.key}_body`] = bodyArr?.length > 0 ? bodyArr[0].value : t.defaultBody;
          }
          form.patchValue(patch);
          this.showProgressSpinner = false;
        },
        err => {
          LoggerService.error(`Error loading ${channel} configuration:`, err);
          this.errorMessage = `Could not load ${channel} configuration.`;
          this.showProgressSpinner = false;
        },
      );
  }

  insertPlaceholder(placeholder: string): void {
    if (this.channel === 'email' && this.rte) {
      this.rte.insertText(placeholder);
      return;
    }
    const ta = document.getElementById(`sms-body-${this.selected.key}`) as HTMLTextAreaElement | null;
    const ctrl = this.form.get(`${this.selected.key}_body`);
    if (!ctrl) return;
    const current: string = ctrl.value || '';
    if (ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart;
      const end = ta.selectionEnd ?? start;
      const next = current.slice(0, start) + placeholder + current.slice(end);
      ctrl.setValue(next);
      setTimeout(() => {
        ta.focus();
        const cursor = start + placeholder.length;
        ta.setSelectionRange(cursor, cursor);
      });
    } else {
      ctrl.setValue(current + placeholder);
    }
  }

  resetToDefault(): void {
    const t = this.selected;
    const patch: Record<string, any> = {
      [`${t.key}_body`]: t.defaultBody,
    };
    if (t.subjectField) {
      patch[`${t.key}_subject`] = t.defaultSubject;
    }
    this.form.patchValue(patch);
  }

  selectedInterval(): string | null {
    const f = this.form;
    if (f.get('enable24HourReminder')?.value) return '24_hour';
    if (f.get('enable48HourReminder')?.value) return '48_hour';
    if (f.get('enable72HourReminder')?.value) return '72_hour';
    return null;
  }

  setInterval(value: string | null): void {
    this.form.patchValue({
      enable24HourReminder: value === '24_hour',
      enable48HourReminder: value === '48_hour',
      enable72HourReminder: value === '72_hour',
    });
  }

  smsBodyLength(): number {
    return (this.form.get(`${this.selected.key}_body`)?.value || '').length;
  }

  smsSegments(): number {
    const len = this.smsBodyLength();
    if (len === 0) return 0;
    return Math.ceil(len / 160);
  }

  save(): void {
    const cfg = this.config;
    const form = this.form;
    const v = form.value;
    const intervals: Array<{ value: string }> = [];
    if (v.enable24HourReminder) intervals.push({ value: '24_hour' });
    if (v.enable48HourReminder) intervals.push({ value: '48_hour' });
    if (v.enable72HourReminder) intervals.push({ value: '72_hour' });

    const configData: Record<string, any> = {};

    for (const tpl of cfg.templates) {
      const enabled = v[`${tpl.key}_enabled`];
      const body = v[`${tpl.key}_body`];
      if (tpl.enableField) {
        configData[tpl.enableField] = enabled ? [{ value: true }] : [];
      }
      configData[tpl.bodyField] = body ? [{ value: body }] : [];
      if (tpl.subjectField) {
        const subject = v[`${tpl.key}_subject`];
        configData[tpl.subjectField] = subject ? [{ value: subject }] : [];
      }
    }
    configData['field_lesson_reminder_intervals'] = intervals;

    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.rest
      .httpPOST('/api_rest/v1/saveStudioConfig', {
        config_field_name: cfg.configFieldName,
        config_data: configData,
      })
      .subscribe(
        () => {
          this.successMessage = `Saved ${this.selected.label} (${this.channel.toUpperCase()}).`;
          this.showProgressSpinner = false;
        },
        err => {
          LoggerService.error('Error saving configuration:', err);
          this.errorMessage = 'Could not save. Please try again.';
          this.showProgressSpinner = false;
        },
      );
  }
}
